import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  ExternalLink, Map, Globe, Loader2, AlertCircle, MapPin,
  CalendarDays, Building2, Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

const coverImg = "/ism-world-map-cover.png";
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface MapPlacement {
  id: number;
  placementType: string;
  title: string;
  description: string | null;
  realWorldAddress: string | null;
  realWorldCity: string | null;
  virtualSpaceName: string | null;
  imageUrl: string | null;
  externalLinkUrl: string | null;
  linkedEventId: number | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
}

const mapUrl = `/map/index.html`;

function placementPriority(placement: MapPlacement) {
  const isFestival = placement.title.toLowerCase().includes("urban film festival");
  if (!isFestival) return 2;
  return placement.placementType === "event_pin" ? 0 : 1;
}

// Placement type badge colours
const TYPE_STYLES: Record<string, string> = {
  event_pin:          "bg-primary/15 text-primary border-primary/30",
  sponsored_landmark: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  ad_billboard:       "bg-blue-500/15 text-blue-400 border-blue-500/30",
  storefront:         "bg-green-500/15 text-green-400 border-green-500/30",
};

const TYPE_LABELS: Record<string, string> = {
  event_pin:          "Event Pin",
  sponsored_landmark: "Sponsored Landmark",
  ad_billboard:       "Ad Billboard",
  storefront:         "Storefront",
};

export default function WorldMap() {
  const [iframeState, setIframeState] = useState<"loading" | "loaded" | "error">("loading");
  const [placements, setPlacements] = useState<MapPlacement[]>([]);
  const [loadingPlacements, setLoadingPlacements] = useState(true);
  const [, navigate] = useLocation();

  // Listen for in-SPA navigation requests posted from the map iframe
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.data?.type === "ISM_NAVIGATE" && typeof e.data.path === "string") {
        navigate(e.data.path);
      }
      if (e.data?.type === "ISM_MAP_ERROR") {
        setIframeState("error");
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate]);

  useEffect(() => {
    fetch(`${BASE}/api/map-placements/active`)
      .then((r) => r.json())
      .then((data) => {
        const activePlacements = Array.isArray(data) ? data : [];
        setPlacements(
          [...activePlacements].sort(
            (a, b) => placementPriority(a) - placementPriority(b),
          ),
        );
      })
      .catch(() => setPlacements([]))
      .finally(() => setLoadingPlacements(false));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Hero ── */}
      <div className="relative w-full h-[340px] md:h-[440px] overflow-hidden">
        <img src={coverImg} alt="ISM New World Map" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 md:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-primary bg-primary/15 border border-primary/30 rounded-full px-3 py-1 mb-3">
              <Map className="w-3 h-3" />
              Featured · ISM Virtual Directory
            </span>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight mb-2">
              ISM New World Map
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed">
              Explore the full interactive ISM universe — digital cities, venues, creator spaces, and live experiences across the virtual world.
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="gap-2 bg-primary hover:bg-primary/80">
                  <ExternalLink className="w-4 h-4" />
                  Open Full Map
                </Button>
              </a>
              <Link href="/map-placement">
                <Button size="sm" variant="outline" className="gap-2 border-primary/40 text-primary hover:bg-primary/10">
                  <MapPin className="w-4 h-4" />
                  Request a Placement
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Interactive Map Preview ── */}
      <div className="px-4 md:px-10 py-4">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-4 h-4 text-primary" />
          <h2 className="font-bold uppercase tracking-widest text-sm">Interactive World Preview</h2>
          <div className="flex-1 h-px bg-border ml-2" />
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Open fullscreen
          </a>
        </div>

        <div
          className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-black"
          style={{ aspectRatio: "16/9", minHeight: 400 }}
        >
          {iframeState === "loading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black z-10 pointer-events-none">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground font-mono">Loading ISM World Map…</p>
            </div>
          )}

          {iframeState === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black z-10 text-center px-8">
              <AlertCircle className="w-10 h-10 text-primary/60" />
              <p className="font-bold text-lg">Map preview unavailable</p>
              <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                This browser couldn't start the 3D globe. All live placements, including the Miami festival pin, remain available below.
              </p>
              <a href="#active-map-placements">
                <Button className="gap-2 mt-2">
                  <MapPin className="w-4 h-4" />
                  View Live Placements
                </Button>
              </a>
            </div>
          )}

          <iframe
            src={mapUrl}
            title="ISM New World Map"
            className="w-full h-full border-0"
            style={{ display: iframeState === "error" ? "none" : "block" }}
            onLoad={() => setIframeState("loaded")}
            onError={() => setIframeState("error")}
            allow="fullscreen; geolocation; autoplay"
          />
        </div>

        <p className="mt-3 text-xs text-muted-foreground text-center">
          Drag to orbit · scroll to zoom ·{" "}
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary/70 hover:text-primary underline underline-offset-2"
          >
            open fullscreen ↗
          </a>
        </p>
      </div>

      {/* ── Active Map Placements ── */}
      <div id="active-map-placements" className="px-4 md:px-10 py-8 scroll-mt-20">
        <div className="flex items-center gap-2 mb-6">
          <MapPin className="w-4 h-4 text-primary" />
          <h2 className="font-bold uppercase tracking-widest text-sm">Active Placements</h2>
          <div className="flex-1 h-px bg-border ml-2" />
          <Link href="/map-placement">
            <span className="text-xs text-primary/70 hover:text-primary cursor-pointer transition-colors">
              + Add Yours
            </span>
          </Link>
        </div>

        {loadingPlacements ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : placements.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No active placements yet.{" "}
            <Link href="/map-placement" className="text-primary underline underline-offset-2">Be the first.</Link>
          </div>
        ) : (
          <div
            className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 md:-mx-10 md:px-10"
            style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
          >
            {placements.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="group relative bg-card/40 border border-border rounded-2xl overflow-hidden hover:border-primary/40 transition-colors flex-none w-72"
                style={{ scrollSnapAlign: "start" }}
              >
                {/* Flyer image */}
                {p.imageUrl ? (
                  <div className="relative h-48 overflow-hidden bg-black">
                    <img
                      src={p.imageUrl}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <span className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono border rounded-full px-2.5 py-0.5 ${TYPE_STYLES[p.placementType] ?? "bg-card border-border text-muted-foreground"}`}>
                      <MapPin className="w-2.5 h-2.5" />
                      {TYPE_LABELS[p.placementType] ?? p.placementType}
                    </span>
                  </div>
                ) : (
                  <div className="h-48 bg-muted/30 flex items-center justify-center">
                    <MapPin className="w-10 h-10 text-muted-foreground/40" />
                  </div>
                )}

                {/* Card body */}
                <div className="p-4">
                  <h3 className="font-semibold text-sm leading-snug mb-2 line-clamp-2">{p.title}</h3>

                  {(p.realWorldAddress || p.realWorldCity) && (
                    <div className="flex items-start gap-1.5 text-xs text-muted-foreground mb-1.5">
                      <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary/60" />
                      <span>{[p.realWorldAddress, p.realWorldCity].filter(Boolean).join(", ")}</span>
                    </div>
                  )}

                  {p.virtualSpaceName && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                      <Globe className="w-3.5 h-3.5 shrink-0 text-primary/60" />
                      <span>{p.virtualSpaceName}</span>
                    </div>
                  )}

                  {p.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                      {p.description}
                    </p>
                  )}

                  {/* CTA — context-aware label */}
                  {p.externalLinkUrl ? (
                    <a href={p.externalLinkUrl} target="_blank" rel="noopener noreferrer" className="w-full">
                      {p.placementType === "storefront" || p.placementType === "ad_billboard" ? (
                        <Button size="sm" className="w-full gap-1.5 text-xs bg-primary/90 hover:bg-primary">
                          <ExternalLink className="w-3.5 h-3.5" />
                          Visit Site
                        </Button>
                      ) : (
                        <Button size="sm" className="w-full gap-1.5 text-xs bg-primary/90 hover:bg-primary">
                          <Ticket className="w-3.5 h-3.5" />
                          Tickets &amp; Packages
                        </Button>
                      )}
                    </a>
                  ) : p.linkedEventId ? (
                    <div className="flex flex-col gap-2">
                      <Link href={`/events/${p.linkedEventId}`}>
                        <Button size="sm" variant="outline" className="w-full gap-1.5 text-xs border-primary/40 text-primary hover:bg-primary/10">
                          <CalendarDays className="w-3.5 h-3.5" />
                          Event Details
                        </Button>
                      </Link>
                      <Link href={`/events/${p.linkedEventId}#tickets`}>
                        <Button size="sm" className="w-full gap-1.5 text-xs bg-primary/90 hover:bg-primary">
                          <Ticket className="w-3.5 h-3.5" />
                          Get Tickets
                        </Button>
                      </Link>
                    </div>
                  ) : null}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* ── Info Strip ── */}
      <div className="px-4 md:px-10 py-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Universe",      value: "ISM New World" },
            { label: "Coverage",      value: "US Digi-Cities" },
            { label: "Environments",  value: "Spatial · IMVU · 3D" },
          ].map(({ label, value }) => (
            <div key={label} className="bg-card/40 border border-border rounded-xl px-5 py-4">
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-mono mb-1">{label}</p>
              <p className="font-semibold text-sm">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
